import omit from 'lodash/omit'
import moment from 'moment'
import { createValidationError, minDuration, required, max, min, maxLength, validate } from 'labguard-cdk'

// validators
const maxLength32 = maxLength(32)
const maxLength11 = maxLength(11)
const minDuration1 = minDuration(moment.duration(1, 'minute').asMilliseconds())
const minDurationAcqPeriod = acqPeriod => value => minDuration(acqPeriod)(value)
const min0 = min(0)
const min1 = min(1)
const max120 = max(120)

const greaterThanHighLimit = highLimit => value => {
  if (highLimit && value < highLimit) {
    return createValidationError('WEB_GREATER_THAN_HIGH_LIMIT')
  }
}
const lessThanHighLimitTempo = highLimitTempo => value => {
  if (highLimitTempo && value > highLimitTempo) {
    return createValidationError('WEB_LESS_THAN_HIGH_LIMIT_TEMPO')
  }
}
const greaterThanSetValue = setValue => value => {
  if (setValue && value < setValue) {
    return createValidationError('WEB_GREATER_THAN_SET_VALUE')
  }
}
const lessThanSetValue = setValue => value => {
  if (setValue && value > setValue) {
    return createValidationError('WEB_LESS_THAN_SET_VALUE')
  }
}
const greaterThanLowLimitTempo = lowLimitTempo => value => {
  if (lowLimitTempo && value < lowLimitTempo) {
    return createValidationError('WEB_GREATER_THAN_LOW_LIMIT_TEMPO')
  }
}
const lessThanLowLimit = lowLimit => value => {
  if (lowLimit && value > lowLimit) {
    return createValidationError('WEB_LESS_THAN_LOW_LIMIT')
  }
}

/**
 * Validates a channel form object
 * @param acqChain the acq chain.
 * @return {Object} An object with the field names which are in error as keys and the corresponding error messages
 * as values. The object will be empty if there was no error.
 */
export const validateChannel = ({ name, shortName, alarmRecall, acqChain, lastThreshold, watching } = {}) => ({
  name: validate([required, maxLength32])(name),
  shortName: maxLength11(shortName),
  alarmRecall: watching && validate([required, minDurationAcqPeriod(acqChain && acqChain.acqPeriod)])(alarmRecall),
  acqChain: validate([required, validateAcqChain])(acqChain),
  lastThreshold: validate([validateLastThreshold])(lastThreshold),
})

const validateAcqChain = ({
  acqPeriod,
  timeWithNoValue,
  calibrationCertificate,
  adjustmentCertificate,
  certificateAlarmDelay,
} = {}) => ({
  acqPeriod: validate([required, minDuration1])(acqPeriod),
  timeWithNoValue: validate([required, minDurationAcqPeriod])(timeWithNoValue),
  certificateAlarmDelay: required(certificateAlarmDelay),
  adjustmentCertificate: adjustmentCertificate && validate([validateAdjustmentCertificate])(adjustmentCertificate),
  calibrationCertificate: calibrationCertificate && validate([validateCalibrationCertificate])(calibrationCertificate),
})

const validateAdjustmentCertificate = ({ adjustmentDate, polynom, ...certificate } = {}) => ({
  adjustmentDate: required(adjustmentDate),
  polynom: required(polynom),
  ...validateCertificate(certificate),
})

const validateCalibrationCertificate = ({ calibrationDate, ...certificate } = {}) => ({
  calibrationDate: required(calibrationDate),
  ...validateCertificate(certificate),
})

const validateCertificate = ({ number, deviation, uncertainty, validityDuration } = {}) => ({
  number: required(number),
  deviation: required(deviation),
  uncertainty: validate([required, min0])(uncertainty),
  validityDuration: validate([required, min1, max120])(validityDuration),
})

const validateLastThreshold = ({
  highTempo,
  highLimitTempo,
  highLimit,
  setValue,
  lowLimit,
  lowLimitTempo,
  lowTempo,
} = {}) => ({
  highTempo: required(highTempo),
  highLimitTempo: validate([required, greaterThanHighLimit(highLimit), greaterThanSetValue(setValue)])(highLimitTempo),
  highLimit: validate([required, greaterThanSetValue(setValue), lessThanHighLimitTempo(highLimitTempo)])(highLimit),
  setValue: required(setValue),
  lowLimit: validate([required, lessThanSetValue(setValue), greaterThanLowLimitTempo(lowLimitTempo)])(lowLimit),
  lowLimitTempo: validate([required, lessThanLowLimit(lowLimit), lessThanSetValue(setValue)])(lowLimitTempo),
  lowTempo: required(lowTempo),
})

/**
 * Converts a channel object to a GraphQL ChannelInput.
 * @param channel The channel
 * @returns {*} The channel GraphQL input.
 */
export const asChannelInput = channel =>
  omit(channel, [
    'id',
    'acqChain.name',
    'acqChain.status',
    'acqChain.unit',
    'acqChain.station',
    'acqChain.modem',
    'acqChain.transmitter',
    'acqChain.sensor',
    'acqChain.calibrationCertificate.id',
    'acqChain.calibrationCertificate.createdAt',
    'acqChain.adjustmentCertificate.id',
    'acqChain.adjustmentCertificate.createdAt',
  ])
